import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Grid, Checkbox, FormControlLabel, Tooltip, TextField, CircularProgress } from '@mui/material';
import { InfoOutlinedIcon, InsertPhotoOutlinedIcon } from 'icons/materialIcons';
import TeamEmailSignatureProfilePicture from './TeamEmailSignatureProfilePicture';
import TeamEmailSignatureCompanyLogo from './TeamEmailSignatureCompanyLogo';
import { useEmailSignatureContext, useInitializeEmailSignature } from 'contexts/EmailSignatureContext';
import { useParams } from 'react-router-dom';
import { getCardById } from 'apis/cardApi';
import TeamEmailSignatureBannerImage from './TeamEmailSignatureBannerImage';
import TeamEmailSignatureQrCodeLogo from './TeamEmailSignatureQrCodeLogo';
import TeamEmailSignatureContent from './TeamEmailSignatureContent';
import { getEmailSignature } from 'team/apis/teamEmailSignatureApi';
import { useTeam } from 'team/contexts/teamContext';
import { Alert, AlertTitle } from '@mui/material';

const TeamEmailSignatureContainer = ({ user }) => {
    const { currentTeamId } = useTeam();
    const { cardId } = useParams();
    const [loading, setLoading] = useState(true);
    const [cardDetails, setCardDetails] = useState({});

    useInitializeEmailSignature(cardId);

    const {
        emailSignatureState,
        setEmailSignatureName,
        setEmailSignatureCompany,
        setEmailSignatureJobTitle,
        setEmailSignaturePhoneNumber,
        setEmailSignatureLocation,
        setEmailSignatureDisclaimer,
        setEmailSignatureBannerImageUrl,
        setEmailSignatureIncludeBannerImage,
        setEmailSignatureIncludeQRCode,
        setEmailSignatureProfilePictureUrl,
        setEmailSignatureCompanyLogoUrl,
        updateEmailSignatureData
    } = useEmailSignatureContext();

    const {
        emailSignatureBannerImageUrl,
        emailSignatureName,
        emailSignatureJobTitle,
        emailSignatureProfilePictureUrl,
        emailSignatureCompany,
        emailSignaturePhoneNumber,
        emailSignatureLocation,
        emailSignatureDisclaimer,
        emailSignatureIncludeBannerImage,
        emailSignatureIncludeQRCode
    } = emailSignatureState;

    const [includeBanner, setIncludeBanner] = useState(emailSignatureState[cardId]?.emailSignatureIncludeBannerImage || false);

    const [includeQRCode, setIncludeQRCode] = useState(emailSignatureState[cardId]?.emailSignatureIncludeQRCode || true); // New state for QR code

    useEffect(() => {
        const fetchCardDetails = async () => {
            try {
                const data = await getCardById(cardId);
                setCardDetails(data);

                // If user has an assigned signature, fetch the template and populate state
                if (user?.assignedSignature?._id && currentTeamId) {
                    try {
                        const template = await getEmailSignature(currentTeamId, user.assignedSignature._id);
                        if (template && template.data) {
                            const sigData = template.data;

                            // Map template fields to email signature context fields
                            // Note: Template data uses some different keys than context state
                            const mappedData = {
                                signatureName: sigData.signatureName,
                                emailSignatureName: sigData.emailSignatureName || '',
                                emailSignatureJobTitle: sigData.emailSignatureJobTitle || '',
                                emailSignatureCompany: sigData.emailSignatureCompany || '',
                                emailSignatureLocation: sigData.emailSignatureLocation || '',
                                emailSignaturePhoneNumber: sigData.emailSignaturePhoneNumber || '',
                                emailSignatureDisclaimer: sigData.emailSignatureDisclaimer || '',
                                emailSignatureBannerImageUrl: sigData.emailSignatureBannerImageUrl || '',
                                emailSignatureProfilePictureUrl: sigData.emailSignatureProfilePictureUrl || '',
                                emailSignatureCompanyLogoUrl: sigData.emailSignatureCompanyLogoUrl || '',
                                emailSignatureIncludeBannerImage: sigData.emailSignatureIncludeBannerImage || false,
                                emailSignatureIncludeQRCode: sigData.emailSignatureIncludeQRCode || true,
                                emailSignatureShowName: sigData.emailSignatureShowName !== undefined ? sigData.emailSignatureShowName : true,
                                emailSignatureShowPronouns: sigData.emailSignatureShowPronouns || false,
                                emailSignatureShowJobTitle: sigData.emailSignatureShowJobTitle !== undefined ? sigData.emailSignatureShowJobTitle : true,
                                emailSignatureShowCompany: sigData.emailSignatureShowCompany !== undefined ? sigData.emailSignatureShowCompany : true,
                                emailSignatureShowLocation: sigData.emailSignatureShowLocation !== undefined ? sigData.emailSignatureShowLocation : true,
                                emailSignatureShowPhoneNumber: sigData.emailSignatureShowPhoneNumber !== undefined ? sigData.emailSignatureShowPhoneNumber : true,
                                emailSignatureShowProfilePic: sigData.emailSignatureShowProfilePic !== undefined ? sigData.emailSignatureShowProfilePic : true,
                                emailSignatureShowCompanyLogo: sigData.emailSignatureShowCompanyLogo !== undefined ? sigData.emailSignatureShowCompanyLogo : true,
                                emailSignatureLinkTypes: sigData.emailSignatureLinkTypes || []
                            };

                            updateEmailSignatureData(cardId, mappedData);
                        }
                    } catch (templateError) {
                        console.error('Error fetching assigned signature template:', templateError);
                    }
                }
            } catch (error) {
                console.error('Error fetching card details:', error);
            } finally {
                setLoading(false);
            }
        };

        if (cardId) {
            fetchCardDetails();
        }
    }, [cardId, user, currentTeamId, updateEmailSignatureData]);



    useEffect(() => {
        if (cardDetails?.card) {
            const { fullName, jobTitle, company, location, phoneNumber } = cardDetails.card.contact_info || {};

            // Update context state only if values have changed and are not undefined
            if (fullName && emailSignatureState[cardId]?.emailSignatureName !== fullName) setEmailSignatureName(cardId, fullName);
            if (jobTitle && emailSignatureState[cardId]?.emailSignatureJobTitle !== jobTitle) setEmailSignatureJobTitle(cardId, jobTitle);
            if (company && emailSignatureState[cardId]?.emailSignatureCompany !== company) setEmailSignatureCompany(cardId, company);
            if (location && emailSignatureState[cardId]?.emailSignatureLocation !== location) setEmailSignatureLocation(cardId, location);
            if (phoneNumber && emailSignatureState[cardId]?.emailSignaturePhoneNumber !== phoneNumber) setEmailSignaturePhoneNumber(cardId, phoneNumber);

            // Set image URLs from card data (for team member level - displays that member's images)
            if (cardDetails.card.profilePictureUrl && emailSignatureState[cardId]?.emailSignatureProfilePictureUrl !== cardDetails.card.profilePictureUrl) {
                setEmailSignatureProfilePictureUrl(cardId, cardDetails.card.profilePictureUrl);
            }
            if (cardDetails.card.companyLogoUrl && emailSignatureState[cardId]?.emailSignatureCompanyLogoUrl !== cardDetails.card.companyLogoUrl) {
                setEmailSignatureCompanyLogoUrl(cardId, cardDetails.card.companyLogoUrl);
            }
        }
    }, [cardDetails, cardId, emailSignatureState, setEmailSignatureName, setEmailSignatureJobTitle, setEmailSignatureCompany, setEmailSignatureLocation, setEmailSignaturePhoneNumber, setEmailSignatureProfilePictureUrl, setEmailSignatureCompanyLogoUrl]);




    const fileInputRef = useRef(null);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const url = URL.createObjectURL(file);
            setEmailSignatureBannerImageUrl(cardId, url);
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        if (file) {
            const url = URL.createObjectURL(file);
            setEmailSignatureBannerImageUrl(cardId, url);
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleClick = () => {
        fileInputRef.current.click();
    };

    const handleIncludeBannerChange = (event) => {
        const { checked } = event.target;
        setIncludeBanner(checked);
        setEmailSignatureIncludeBannerImage(cardId, checked);
    };


    const handleIncludeQRCodeChange = (event) => {
        const { checked } = event.target;
        setIncludeQRCode(checked);
        setEmailSignatureIncludeQRCode(cardId, checked);
    };




    useEffect(() => {
        console.log('Email Signature State:', emailSignatureState);
        if (emailSignatureState[cardId]) {
            console.log('Email Signature Details from the container for Card ID:', cardId);
            console.log('Profile Picture URL:', emailSignatureState[cardId]?.emailSignatureProfilePictureUrl); // Log Profile Picture URL
            // Other console logs
            console.log('Name:', emailSignatureState[cardId]?.emailSignatureName);
            console.log('Company:', emailSignatureState[cardId]?.emailSignatureCompany);
            console.log('Job Title:', emailSignatureState[cardId]?.emailSignatureJobTitle);
            console.log('Phone Number:', emailSignatureState[cardId]?.emailSignaturePhoneNumber);
            console.log('Location:', emailSignatureState[cardId]?.emailSignatureLocation);
            console.log('Disclaimer:', emailSignatureState[cardId]?.emailSignatureDisclaimer);
            console.log('Banner Image URL:', emailSignatureState[cardId]?.emailSignatureBannerImageUrl);
            console.log('Include Banner:', emailSignatureState[cardId]?.emailSignatureIncludeBannerImage);
            console.log('Include QR Code:', emailSignatureState[cardId]?.emailSignatureIncludeQRCode);
        }
    }, [emailSignatureState, cardId]);





    return (
        <Box my={4} sx={{ height: user?.assignedSignature ? 'calc(100vh - 200px)' : 'auto', display: 'flex', flexDirection: 'column' }}>
            {!user?.assignedSignature && (
                <Typography variant="body2"
                    sx={{
                        fontSize: '1.1rem', fontWeight: 600, color: '#4f4f4f', marginBottom: '10px', paddingLeft: 2
                    }}
                    gutterBottom align="left">
                    Email Signature
                </Typography>
            )}

            {loading ? (
                <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 2,
                        maxWidth: 800,
                        margin: 'auto',
                        width: '100%',
                        flexGrow: 1,
                        justifyContent: user?.assignedSignature ? 'center' : 'flex-start',
                        alignItems: user?.assignedSignature ? 'center' : 'stretch'
                    }}
                >
                    {user?.assignedSignature ? (
                        <Box sx={{
                            mt: '0.0625rem', // 1px
                            p: 2,
                            border: '0.0625rem solid #e0e0e0', // 1px
                            borderRadius: 2,
                            backgroundColor: '#ffffff',
                            width: '100%',
                            maxWidth: '30rem', // 800px
                            boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.05)' // 0 2px 8px
                        }}>
                            <TeamEmailSignatureContent
                                emailSignatureState={emailSignatureState}
                                cardId={cardId}
                                setLoading={setLoading}
                                setError={() => { }}
                            />
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={4} md={3}>
                                    <TeamEmailSignatureProfilePicture />
                                </Grid>
                                <Grid item xs={12} sm={4} md={3}>
                                    <TeamEmailSignatureCompanyLogo />
                                </Grid>
                                <Grid item xs={12} sm={4} md={3}>
                                    <TeamEmailSignatureQrCodeLogo />
                                </Grid>
                                <Grid item xs={12} sm={4} md={3}></Grid>
                            </Grid>

                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={includeBanner}
                                            onChange={handleIncludeBannerChange}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#4f4f4f' }}>
                                            Include Banner
                                        </Typography>
                                    }
                                />
                                <Tooltip title="Ideal Dimensions: 780 px by 300 px (2.6:1)" arrow>
                                    <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#4f4f4f' }} />
                                </Tooltip>
                            </Box>

                            <Box sx={{ mb: 4 }}>
                                <TeamEmailSignatureBannerImage />
                            </Box>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                                        Name</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        value={emailSignatureState[cardId]?.emailSignatureName || ''}
                                        onChange={(e) => setEmailSignatureName(cardId, e.target.value)}
                                        sx={{
                                            backgroundColor: '#f7f7f7',
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { border: 'none' },
                                                '&:hover fieldset': { border: 'none' },
                                                '&.Mui-focused fieldset': { border: 'none' },
                                            },
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", mt: 2 }}>
                                        Job Title</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        value={emailSignatureState[cardId]?.emailSignatureJobTitle || ''}
                                        onChange={(e) => setEmailSignatureJobTitle(cardId, e.target.value)}
                                        sx={{
                                            backgroundColor: '#f7f7f7',
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { border: 'none' },
                                                '&:hover fieldset': { border: 'none' },
                                                '&.Mui-focused fieldset': { border: 'none' },
                                            },
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", mt: 2 }}>
                                        Company</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        value={emailSignatureState[cardId]?.emailSignatureCompany || ''}
                                        onChange={(e) => setEmailSignatureCompany(cardId, e.target.value)}
                                        sx={{
                                            backgroundColor: '#f7f7f7',
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { border: 'none' },
                                                '&:hover fieldset': { border: 'none' },
                                                '&.Mui-focused fieldset': { border: 'none' },
                                            },
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", mt: 2 }}>
                                        Phone Number</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        value={emailSignatureState[cardId]?.emailSignaturePhoneNumber || ''}
                                        onChange={(e) => setEmailSignaturePhoneNumber(cardId, e.target.value)}
                                        sx={{
                                            backgroundColor: '#f7f7f7',
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { border: 'none' },
                                                '&:hover fieldset': { border: 'none' },
                                                '&.Mui-focused fieldset': { border: 'none' },
                                            },
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", mt: 2 }}>
                                        Location</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        value={emailSignatureState[cardId]?.emailSignatureLocation || ''}
                                        onChange={(e) => setEmailSignatureLocation(cardId, e.target.value)}
                                        sx={{
                                            backgroundColor: '#f7f7f7',
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { border: 'none' },
                                                '&:hover fieldset': { border: 'none' },
                                                '&.Mui-focused fieldset': { border: 'none' },
                                            },
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", mt: 2 }}>
                                        Disclaimer</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        value={emailSignatureState[cardId]?.emailSignatureDisclaimer || ''}
                                        onChange={(e) => setEmailSignatureDisclaimer(cardId, e.target.value)}
                                        sx={{
                                            backgroundColor: '#f7f7f7',
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { border: 'none' },
                                                '&:hover fieldset': { border: 'none' },
                                                '&.Mui-focused fieldset': { border: 'none' },
                                            },
                                        }}
                                        multiline
                                        rows={2}
                                    />
                                </Grid>
                            </Grid>

                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={includeQRCode}
                                            onChange={handleIncludeQRCodeChange}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#4f4f4f' }}>
                                            Include QRCode
                                        </Typography>
                                    }
                                />
                            </Box>
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default TeamEmailSignatureContainer;
